import { Button, Tooltip } from '@mui/material';
import cn from 'classnames';
import { FormikProps } from 'formik';
import * as React from 'react';

import { ReactComponent as IconUser } from '../../../assets/images/icon-user.svg';

import { Input, Loader, Textarea } from '../../../components';
import { RECEIVER_EMAIL, RECEIVER_FIRST_NAME, RECEIVER_LAST_NAME, RECEIVER_PHONE } from '../../../constants/shipping';
import { MAX_BULK_NOTE_LENGTH, MAX_NOTE_LENGTH, MAX_ONE_LINK_NOTE_LENGTH, MESSAGE } from '../../../constants/templates';
import { IMaintenanceFormOutputData } from '../../../types/forms';
import { IReceiverInfoDetailsFormValues } from '../../../types/shipping';

import styles from './SendDetailsForm.module.scss';

export interface ISendSummaryFormProps extends IMaintenanceFormOutputData {
  onChange: (
    name: keyof IReceiverInfoDetailsFormValues,
    value: string | number | undefined | string[] | boolean,
  ) => void;
  onCancel?: () => void;
  formRef?: React.Ref<HTMLFormElement>;
  form: FormikProps<IReceiverInfoDetailsFormValues>;
  requiredFields: Record<string, boolean>;
  disabled?: boolean;
  className?: string;
  isOneLinkEngagement?: boolean;
  isBulkEngagement?: boolean;
  isEditingShipmentOptionsAllowed?: boolean;
  isNoteHidden?: boolean;
  isDirectSend?: boolean;
  isAddressFixed?: boolean;
  isDelayedShipping?: boolean;
  isLoading?: boolean;
}

interface IFormSectionTitleProps {
  title: string;
  titleClassName?: string;
}

interface IFormRowProps {
  children: React.ReactElement[] | React.ReactElement | string;
  rowClassName?: string;
}

const SendDetailsForm: React.FC<ISendSummaryFormProps> = ({
  onChange,
  onCancel,
  formRef,
  form,
  disabled,
  requiredFields,
  className,
  isOneLinkEngagement,
  isBulkEngagement,
  isNoteHidden,
  isDirectSend,
  isLoading,
}) => {
  const FormSectionTitle = React.useMemo(
    () =>
      ({ title, titleClassName }: IFormSectionTitleProps) => {
        return <div className={cn(styles.sectionTitle, titleClassName)}>{title}</div>;
      },
    [],
  );

  const FormInputRow = React.useMemo(
    () =>
      ({ children, rowClassName }: IFormRowProps) => {
        return <div className={cn(styles.row, rowClassName)}>{children}</div>;
      },
    [],
  );

  const maxChars = React.useMemo(() => {
    switch (true) {
      case isBulkEngagement:
        return MAX_BULK_NOTE_LENGTH;
      case isOneLinkEngagement:
        return MAX_ONE_LINK_NOTE_LENGTH;

      default:
        return MAX_NOTE_LENGTH;
    }
  }, [isBulkEngagement, isOneLinkEngagement]);

  const getFieldErrors = React.useCallback(
    (fieldName: keyof IReceiverInfoDetailsFormValues) => {
      return form.errors[fieldName];
    },
    [form.errors],
  );

  const handleFormFieldChange = React.useCallback(
    (
      e: React.ChangeEvent<HTMLInputElement> | React.ChangeEvent<HTMLTextAreaElement>,
      name: keyof IReceiverInfoDetailsFormValues,
    ) => {
      onChange(name, e.target.value);
    },
    [onChange],
  );

  const emailFieldRef = React.useRef<HTMLDivElement>(null);
  const [isEmailTooltipOpen, setIsEmailTooltipOpen] = React.useState(false);

  const handleEmailTooltipOpen = React.useCallback(() => {
    const input = emailFieldRef.current?.querySelector('input');
    setIsEmailTooltipOpen(!!input && input.scrollWidth > input.clientWidth);
  }, []);

  const handleEmailTooltipClose = React.useCallback(() => setIsEmailTooltipOpen(false), []);

  const buttonStyles = { fontSize: '15px', lineHeight: 1.7, py: 1, px: 2.75 };

  return (
    <form ref={formRef} onSubmit={form.handleSubmit} className={cn(styles.shippingForm, className)}>
      <Loader isLoading={isLoading} />
      <div className={styles.stickyHeader}>
        <FormSectionTitle title="Recipient's details" />
      </div>
      <div className={styles.scrollableContent}>
        <FormInputRow>
          <Input
            helperText="First name"
            name={RECEIVER_FIRST_NAME}
            inputClassName={styles.inputField}
            placeholder="First name"
            disabled={disabled}
            required={requiredFields[RECEIVER_FIRST_NAME]}
            icon={<IconUser />}
            value={form.values[RECEIVER_FIRST_NAME]}
            onChange={(e) => handleFormFieldChange(e, RECEIVER_FIRST_NAME)}
            error={getFieldErrors(RECEIVER_FIRST_NAME)}
          />
          <Input
            helperText="Last name"
            name={RECEIVER_LAST_NAME}
            inputClassName={styles.inputField}
            placeholder="Last name"
            disabled={disabled}
            required={requiredFields[RECEIVER_LAST_NAME]}
            icon={<IconUser />}
            onChange={(e) => handleFormFieldChange(e, RECEIVER_LAST_NAME)}
            value={form.values[RECEIVER_LAST_NAME]}
            error={getFieldErrors(RECEIVER_LAST_NAME)}
          />
        </FormInputRow>

        <FormInputRow>
          <Input
            helperText="Phone"
            name={RECEIVER_PHONE}
            inputClassName={styles.inputField}
            placeholder="Phone"
            disabled={disabled}
            required={requiredFields[RECEIVER_PHONE]}
            value={form.values[RECEIVER_PHONE]}
            onChange={(e) => handleFormFieldChange(e, RECEIVER_PHONE)}
            error={getFieldErrors(RECEIVER_PHONE)}
          />
        </FormInputRow>

        <FormInputRow>
          <Tooltip
            title={form.values[RECEIVER_EMAIL] ?? ''}
            open={isEmailTooltipOpen}
            onOpen={handleEmailTooltipOpen}
            onClose={handleEmailTooltipClose}
            placement="top"
          >
            <div ref={emailFieldRef} className={styles.fullWidth}>
              <Input
                helperText="Email"
                name={RECEIVER_EMAIL}
                inputClassName={styles.inputField}
                placeholder="Email"
                disabled={disabled}
                required={requiredFields[RECEIVER_EMAIL]}
                value={form.values[RECEIVER_EMAIL]}
                onChange={(e) => handleFormFieldChange(e, RECEIVER_EMAIL)}
                error={getFieldErrors(RECEIVER_EMAIL)}
              />
            </div>
          </Tooltip>
        </FormInputRow>
        {!isDirectSend && !isNoteHidden && (
          <>
            <FormSectionTitle title="Custom message" titleClassName={styles.secondaryTitle} />
            <FormInputRow>
              <Textarea
                name={MESSAGE}
                value={form.values[MESSAGE]}
                onChange={(e) => handleFormFieldChange(e, MESSAGE)}
                className={styles.inputField}
                inputClassName={styles.noteTextArea}
                placeholder="Message"
                showCounter={true}
                maxCharsCount={maxChars}
              />
            </FormInputRow>
          </>
        )}
      </div>
      <div className={styles.stickyFooter}>
        <FormInputRow rowClassName={styles.controlsContainer}>
          <Button variant="outlined" size="small" onClick={onCancel} sx={buttonStyles} data-action="cancel_btn">
            Cancel
          </Button>
          <Button
            type="submit"
            variant="contained"
            size="small"
            disabled={!form.dirty || !!Object.keys(form.errors).length || form.isSubmitting}
            sx={buttonStyles}
            data-action="save_changes_btn"
          >
            Save changes
          </Button>
        </FormInputRow>
      </div>
    </form>
  );
};

export default SendDetailsForm;
